import { MAX_LISTED_IDS } from '@lib/inspections/filters';
import { useLocalization } from '@lib/localization';
import {
  Autocomplete, autocompleteClasses, Avatar, Box, Button, createFilterOptions, InputBase, ListSubheader, Popover,
  type PopperProps, Tooltip, Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { gray } from '@theme/primitives';
import { Check, ChevronDown, Search } from 'lucide-react';
import { type ReactNode, type SyntheticEvent, useId, useMemo, useRef, useState } from 'react';
import {
  optionCheckStyles, pillDividerStyles, pillStyles, pillValueStyles, popoverFooterStyles, popoverPaperStyles,
} from './filters.styles';

export interface FacetOption {
  id: string;
  label: string;
  /** A second line under the label: the username beside a name. */
  secondary?: string;
  /** The heading the option is listed under; options without one are listed without headings. */
  group?: string;
  /** Draws the label's initials before it, for a list of people. */
  avatar?: boolean;
}

interface FacetFilterProps {
  /** What the pill is named, and what its popover says it filters. */
  label: string;
  icon: ReactNode;
  options: FacetOption[];
  selected: string[];
  onChange: (ids: string[]) => void;
  searchPlaceholder: string;
  loading?: boolean;
}

const initialsOf = (name: string): string =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toLocaleUpperCase();

/**
 * The listbox drawn in place, under the search field, instead of floating over it: the
 * popover is the floating thing, and an Autocomplete that is always open is a searchable
 * checklist with the keyboard handling already done.
 */
const InlineListRoot = styled('div')(({ theme }) => ({
  // The theme's paper colour carries `!important`, and it is the popover's own, so only the
  // frame is taken off.
  [`& .${autocompleteClasses.paper}`]: {
    margin: 0,
    border: 'none',
    borderRadius: 0,
    boxShadow: 'none',
  },
  [`& .${autocompleteClasses.listbox}`]: {
    padding: 6,
    maxHeight: 300,
    [`& .${autocompleteClasses.option}`]: {
      gap: 10,
      minHeight: 36,
      padding: '6px 8px',
      borderRadius: theme.shape.borderRadius,
      '&[aria-selected="true"]': { backgroundColor: 'transparent' },
      [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]: {
        backgroundColor: gray[800],
      },
    },
  },
  [`& .${autocompleteClasses.noOptions}, & .${autocompleteClasses.loading}`]: {
    padding: '14px 12px',
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: { position: 'relative' },
}));

const InlinePopper = ({ className, children }: PopperProps) => (
  <InlineListRoot className={className}>{children as ReactNode}</InlineListRoot>
);

/**
 * Options in the order the popover shows them: what was chosen when it opened comes first,
 * within its group where there are groups (the list needs a group's options together, or a
 * heading repeats). What counts as chosen is taken once per opening, so an option toggled
 * while it is open stays where it was rather than jumping away from the pointer.
 */
const orderOptions = (options: FacetOption[], chosen: Set<string>): FacetOption[] => {
  const grouped = options.some((option) => option.group !== undefined);
  const groups = grouped ? [...new Set(options.map((option) => option.group ?? ''))] : [''];

  return groups.flatMap((group) => {
    const inGroup = grouped ? options.filter((option) => (option.group ?? '') === group) : options;

    return [...inGroup.filter((option) => chosen.has(option.id)), ...inGroup.filter((option) => !chosen.has(option.id))];
  });
};

// A person is found by their username as well as their name.
const filterOptions = createFilterOptions<FacetOption>({ stringify: (option) => `${option.label} ${option.secondary ?? ''}` });

/**
 * One facet of the inspection list — projects, users or teams — as a pill that opens a
 * searchable checklist. Any number of values may be chosen, and they are alternatives:
 * two projects mean an inspection in either. The toolbar joins the facets with AND.
 *
 * Set, the pill carries how many values it holds, not their names: a name makes it as wide
 * as the name, and a pill that grew pushed the toolbar onto a second line and the list down
 * under it. The names are in its tooltip.
 */
const FacetFilter: React.FC<FacetFilterProps> = ({
  label, icon, options, selected, onChange, searchPlaceholder, loading = false,
}) => {
  const { t } = useLocalization();
  const popoverId = useId();

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [chosenAtOpen, setChosenAtOpen] = useState<Set<string>>(new Set());
  // Held here, as MUI empties the field on every pick: the second of two people with one
  // surname would otherwise have to be searched for again.
  const [query, setQuery] = useState('');
  const searchField = useRef<HTMLInputElement>(null);

  const byId = useMemo(() => new Map(options.map((option) => [option.id, option])), [options]);

  // Ids the URL holds but no option answers (a project since deleted, a link from someone who
  // sees more) still count: they narrow the list, so the pill says so and a change made in
  // the list keeps them. Only "Clear" takes them off, since no row stands for them.
  const selectedOptions = selected.flatMap((id) => byId.get(id) ?? []);
  const unknownIds = selected.filter((id) => !byId.has(id));

  const listed = orderOptions(options, chosenAtOpen);
  const active = selected.length > 0;

  // The endpoint takes no more ids than this in one list. Once a pill holds that many, the
  // options not chosen are offered no more, and a chosen one can still be taken off.
  const chosen = useMemo(() => new Set(selected), [selected]);
  const full = selected.length >= MAX_LISTED_IDS;

  const open = (event: React.MouseEvent<HTMLElement>) => {
    setChosenAtOpen(new Set(selected));
    setQuery('');
    setAnchor(event.currentTarget);
  };

  const close = () => setAnchor(null);

  const handleChange = (event: SyntheticEvent, value: FacetOption[], reason: string) => {
    // Backspace in the search field would otherwise take the last value off, which nobody
    // means while the values are not even drawn beside the field.
    if (reason === 'removeOption' && event.type === 'keydown'
      && ['Backspace', 'Delete'].includes((event as React.KeyboardEvent).key)) return;

    onChange([...unknownIds, ...value.map((option) => option.id)]);
  };

  return (
    <>
      {/* `describeChild`: without it the tooltip's title becomes the button's name, so with a
          team chosen the Teams pill was read as that team alone. */}
      <Tooltip title={active ? selectedOptions.map((option) => option.label).join(', ') : ''} describeChild>
        <Button
          variant="outlined"
          size="small"
          onClick={open}
          aria-haspopup="dialog"
          aria-expanded={Boolean(anchor)}
          aria-controls={anchor ? popoverId : undefined}
          sx={pillStyles(active)}
        >
          {icon}
          <span>{label}</span>

          {active && (
            <>
              <Box component="span" sx={pillDividerStyles} />
              <Box component="span" sx={pillValueStyles}>{selected.length}</Box>
            </>
          )}

          <ChevronDown size={14} style={{ marginLeft: -2 }} />
        </Button>
      </Tooltip>

      <Popover
        id={popoverId}
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { sx: { ...popoverPaperStyles, width: 300 }, role: 'dialog', 'aria-label': label },
          // Given the keyboard once the popover is in, whatever order the effects run in: under
          // StrictMode the focus trap's second run took it from the field's `autoFocus` and
          // left it on the paper.
          transition: { onEntered: () => searchField.current?.focus() },
        }}
      >
        <Autocomplete<FacetOption, true, false, false>
          open
          multiple
          disablePortal
          disableCloseOnSelect
          value={selectedOptions}
          onChange={handleChange}
          inputValue={query}
          onInputChange={(_event, text, reason) => {
            if (reason === 'input' || reason === 'clear') setQuery(text);
          }}
          onClose={(_event, reason) => reason === 'escape' && close()}
          options={listed}
          filterOptions={filterOptions}
          groupBy={listed.some((option) => option.group !== undefined) ? (option) => option.group ?? '' : undefined}
          getOptionLabel={(option) => option.label}
          getOptionDisabled={(option) => full && !chosen.has(option.id)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={loading}
          loadingText={t('Inspections_Filter_Loading')}
          noOptionsText={t('Inspections_Filter_NoOptions')}
          renderTags={() => null}
          slots={{ popper: InlinePopper }}
          renderGroup={(params) => (
            <li key={params.key}>
              {params.group && (
                <ListSubheader
                  component="div"
                  sx={{ top: -6, bgcolor: 'background.paper', color: 'text.secondary', px: 1, pt: 1, pb: 0.5, lineHeight: 1.6 }}
                >
                  {params.group}
                </ListSubheader>
              )}
              <Box component="ul" sx={{ p: 0 }}>{params.children}</Box>
            </li>
          )}
          renderOption={(props, option, { selected: checked }) => {
            const { key, ...optionProps } = props;

            return (
              <li key={key} {...optionProps}>
                <Box sx={optionCheckStyles(checked)}>{checked && <Check size={12} strokeWidth={3} />}</Box>

                {option.avatar && (
                  <Avatar sx={{
                    width: 24, height: 24, fontSize: 11, fontWeight: 600, bgcolor: gray[700], color: gray[100], flexShrink: 0,
                  }}>
                    {initialsOf(option.label)}
                  </Avatar>
                )}

                <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2" noWrap sx={{ fontSize: 13.5 }}>{option.label}</Typography>
                  {option.secondary && (
                    <Typography variant="caption" color="text.secondary" noWrap>{option.secondary}</Typography>
                  )}
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <Box
              ref={params.InputProps.ref}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, px: 1.5, height: 44,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                '& svg': { color: gray[400], flexShrink: 0 },
              }}
            >
              <Search size={16} />
              <InputBase
                inputProps={params.inputProps}
                inputRef={searchField}
                autoFocus
                placeholder={searchPlaceholder}
                sx={{ flex: 1, fontSize: 13.5 }}
              />
            </Box>
          )}
        />

        <Box sx={popoverFooterStyles}>
          <Typography variant="caption" color="text.secondary">
            {/* A plural entry: called with `{ count }`, as one with an array renders the raw key. */}
            {active ? t('Inspections_Filter_Selected', { count: selected.length }) : t('Inspections_Filter_PickAny')}
          </Typography>
          <Button size="small" variant="text" disabled={!active} onClick={() => onChange([])} sx={{ height: 28, px: 1 }}>
            {t('Inspections_Filter_ClearSelection')}
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default FacetFilter;
